import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';
import { api } from '../api/client';
import { Badge, ReachDot, EmptyState, ErrorState, Skeleton, useFetch } from '../components/ui';
import { TimeseriesChart } from '../components/charts';

/* ================================================================== */
/* Types — mirrors plugins/voip/src/magnus.ts and the /live route      */
/* ================================================================== */

interface MagnusOnlineCall {
  user: string;
  sipAccount: string;
  callerId: string;
  destination: string;
  trunk: string;
  status: string;
  durationSec: number;
  codec: string;
}

interface MagnusLive {
  ts: number;
  status: {
    users: number;
    usersNegative: number;
    sipAccounts: number;
    callsOnline: number;
    callsAnswered: number;
    callsRinging: number;
    trunks: number;
    trunksInactive: string[];
    callsToday: number;
    callsByTrunk: Record<string, number>;
    onlineCalls: MagnusOnlineCall[];
  };
  vitals: {
    cpuPct: number | null;
    memUsedPct: number | null;
    disks: Array<{ mount: string; usedPct: number; sizeBytes: number }>;
    uptimeSec: number | null;
  } | null;
  vitalsError: string | null;
}

const REFRESH_OPTIONS = [
  { sec: 5, label: '5s' },
  { sec: 10, label: '10s' },
  { sec: 30, label: '30s' },
  { sec: 60, label: '1 min' },
];
const WINDOW_OPTIONS = [
  { min: 15, label: '15 min' },
  { min: 60, label: '1 hr' },
];

function fmtDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

function fmtBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i < 3 ? 0 : 1)} ${units[i]}`;
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((sec % 3600) / 60)}m`;
}

const pctColor = (pct: number | null, warn: number) =>
  pct === null ? undefined : pct >= 95 ? 'var(--itr-critical)' : pct >= warn ? 'var(--itr-warning)' : undefined;

const isAnswered = (status: string) => /^(up|answer|answered)$/i.test(status);

/* ================================================================== */
/* Page                                                                */
/* ================================================================== */

export function MagnusMonitor() {
  const { id } = useParams<{ id: string }>();

  const [refreshSec, setRefreshSec] = useState(5);
  const [windowMin, setWindowMin] = useState(15);
  const [paused, setPaused] = useState(false);

  const [samples, setSamples] = useState<MagnusLive[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { data: resource } = useFetch(
    () => api.get<{ id: string; name: string; health: string; config: Record<string, unknown> }>(`/api/resources/${id}`),
    [id],
  );
  const diskWarn = Number(resource?.config.diskWarnPct ?? 85);
  const memWarn = Number(resource?.config.memWarnPct ?? 90);

  const maxPoints = Math.max(10, Math.ceil((windowMin * 60) / refreshSec));

  useEffect(() => {
    if (!id || paused) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const live = await api.get<MagnusLive>(`/api/plugins/voip/magnus/${id}/live`);
        if (cancelled) return;
        setError(null);
        setLoading(false);
        setSamples((current) => [...current, live].slice(-maxPoints));
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        // Keep the last good reading on screen; one missed API call is not an outage.
        setError(err instanceof Error ? err.message : 'Could not reach MagnusBilling');
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), refreshSec * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id, refreshSec, paused, maxPoints]);

  const latest = samples[samples.length - 1];

  if (loading && samples.length === 0 && !error) {
    return (
      <div className="col" style={{ gap: 14 }}>
        <Skeleton height={90} />
        <Skeleton height={320} />
      </div>
    );
  }
  if (error && samples.length === 0) return <ErrorState error={error} onRetry={() => setError(null)} />;
  if (!latest) return <EmptyState title="No data yet" text="Waiting for the first reading from MagnusBilling." />;

  const s = latest.status;
  const v = latest.vitals;
  const worstDisk = v?.disks.reduce<(typeof v.disks)[number] | null>((w, d) => (!w || d.usedPct > w.usedPct ? d : w), null) ?? null;
  const trunksActive = s.trunks - s.trunksInactive.length;

  const callSeries = [
    { name: 'Online', points: samples.map((x) => [x.ts, x.status.callsOnline] as [number, number | null]) },
    { name: 'Answered', points: samples.map((x) => [x.ts, x.status.callsAnswered] as [number, number | null]) },
  ];
  const vitalSeries = v
    ? [
        { name: 'CPU', points: samples.map((x) => [x.ts, x.vitals?.cpuPct ?? null] as [number, number | null]) },
        { name: 'Memory', points: samples.map((x) => [x.ts, x.vitals?.memUsedPct ?? null] as [number, number | null]) },
      ]
    : [];
  const calls = [...s.onlineCalls].sort((a, b) => b.durationSec - a.durationSec);
  const byTrunk = Object.entries(s.callsByTrunk).sort((a, b) => b[1] - a[1]);

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to={`/resources/${id}`}>
              {resource && <ReachDot health={resource.health} />}
              {resource?.name ?? 'Resource'}
            </Link>
          </div>
          <h1 className="page-title">MagnusBilling Live</h1>
          <div className="page-subtitle">
            {error ? (
              <span style={{ color: 'var(--itr-critical)' }}>{error} — showing last good reading</span>
            ) : (
              <>
                <span className="health" data-health="up" style={{ ['--health-color' as string]: 'var(--itr-up)' }}>
                  <span className="health-dot" />
                </span>{' '}
                live — {new Date(latest.ts).toLocaleTimeString()}
                {v?.uptimeSec != null && ` · up ${fmtUptime(v.uptimeSec)}`}
              </>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Refresh
            <select className="input" style={{ padding: '4px 8px' }} value={refreshSec} onChange={(e) => setRefreshSec(Number(e.target.value))}>
              {REFRESH_OPTIONS.map((o) => (
                <option key={o.sec} value={o.sec}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Window
            <select className="input" style={{ padding: '4px 8px' }} value={windowMin} onChange={(e) => setWindowMin(Number(e.target.value))}>
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.min} value={o.min}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm" onClick={() => setPaused((p) => !p)}>
            {paused ? (
              <>
                <Play size={13} /> Resume
              </>
            ) : (
              <>
                <Pause size={13} /> Pause
              </>
            )}
          </button>
        </div>
      </div>

      {latest.vitalsError && (
        <div className="card" style={{ padding: '10px 14px', fontSize: 13, borderLeft: '3px solid var(--itr-warning)' }}>
          Disk and memory could not be read over SSH: {latest.vitalsError}
        </div>
      )}

      <div className="stat-strip">
        <div className="stat-chip">
          <div className="stat-chip-label">Calls online</div>
          <div className="stat-chip-value">{s.callsOnline}</div>
          <div className="stat-chip-sub">
            {s.callsAnswered} answered · {s.callsRinging} ringing
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Calls today</div>
          <div className="stat-chip-value">{s.callsToday.toLocaleString()}</div>
          <div className="stat-chip-sub">since midnight</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Customers</div>
          <div className="stat-chip-value">{s.users.toLocaleString()}</div>
          <div className="stat-chip-sub" style={{ color: s.usersNegative > 0 ? 'var(--itr-warning)' : undefined }}>
            {s.usersNegative} below zero · {s.sipAccounts.toLocaleString()} SIP accounts
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Trunks active</div>
          <div className="stat-chip-value" style={{ color: trunksActive === 0 && s.trunks > 0 ? 'var(--itr-critical)' : undefined }}>
            {trunksActive}
          </div>
          <div className="stat-chip-sub">of {s.trunks}</div>
        </div>
        {v && (
          <>
            <div className="stat-chip">
              <div className="stat-chip-label">CPU</div>
              <div className="stat-chip-value">{v.cpuPct === null ? '—' : `${v.cpuPct.toFixed(0)}%`}</div>
              <div className="stat-chip-sub">load ÷ cores</div>
            </div>
            <div className="stat-chip">
              <div className="stat-chip-label">Memory</div>
              <div className="stat-chip-value" style={{ color: pctColor(v.memUsedPct, memWarn) }}>
                {v.memUsedPct === null ? '—' : `${v.memUsedPct.toFixed(0)}%`}
              </div>
              <div className="stat-chip-sub">used, excluding cache</div>
            </div>
            <div className="stat-chip">
              <div className="stat-chip-label">Fullest disk</div>
              <div className="stat-chip-value" style={{ color: pctColor(worstDisk?.usedPct ?? null, diskWarn) }}>
                {worstDisk ? `${worstDisk.usedPct.toFixed(0)}%` : '—'}
              </div>
              <div className="stat-chip-sub mono">{worstDisk?.mount ?? ''}</div>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Calls in progress</span>
          <span className="grow" />
          <span className="subtle" style={{ fontSize: 12 }}>
            {calls.length} call{calls.length === 1 ? '' : 's'}
          </span>
        </div>
        {calls.length === 0 ? (
          <div className="subtle" style={{ padding: '14px 16px', fontSize: 13 }}>
            No calls right now.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>SIP account</th>
                  <th>Caller ID</th>
                  <th>Destination</th>
                  <th>Trunk</th>
                  <th>Status</th>
                  <th className="nums">Duration</th>
                  <th>Codec</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c, i) => (
                  <tr key={`${c.sipAccount}-${c.destination}-${i}`}>
                    <td>{c.user || '—'}</td>
                    <td className="mono">{c.sipAccount || '—'}</td>
                    <td className="mono">{c.callerId || '—'}</td>
                    <td className="mono">{c.destination || '—'}</td>
                    <td className="mono">{c.trunk || '—'}</td>
                    <td>
                      <Badge intent={isAnswered(c.status) ? 'good' : 'info'}>{c.status || '?'}</Badge>
                    </td>
                    <td className="nums">{fmtDuration(c.durationSec)}</td>
                    <td className="mono subtle">{c.codec || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Concurrent calls</span>
          </div>
          <div className="card-body">
            <TimeseriesChart series={callSeries} unit="count" height={200} />
          </div>
        </div>
        {v && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">CPU & memory</span>
            </div>
            <div className="card-body">
              <TimeseriesChart series={vitalSeries} unit="percent" height={200} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        {v && v.disks.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Disks</span>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Mount</th>
                    <th className="nums">Size</th>
                    <th style={{ width: '45%' }}>Used</th>
                  </tr>
                </thead>
                <tbody>
                  {v.disks.map((d) => (
                    <tr key={d.mount}>
                      <td className="mono">{d.mount}</td>
                      <td className="nums">{fmtBytes(d.sizeBytes)}</td>
                      <td>
                        <div className="row" style={{ gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--itr-surface-sunken)', overflow: 'hidden' }}>
                            <div
                              style={{
                                width: `${Math.min(100, d.usedPct)}%`,
                                height: '100%',
                                background: pctColor(d.usedPct, diskWarn) ?? 'var(--itr-primary)',
                              }}
                            />
                          </div>
                          <span className="nums" style={{ minWidth: 38, textAlign: 'right', color: pctColor(d.usedPct, diskWarn) }}>
                            {d.usedPct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Trunks</span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Trunk</th>
                  <th className="nums">Calls now</th>
                </tr>
              </thead>
              <tbody>
                {byTrunk.map(([trunk, n]) => (
                  <tr key={trunk}>
                    <td className="mono">{trunk}</td>
                    <td className="nums">{n}</td>
                  </tr>
                ))}
                {s.trunksInactive.map((t) => (
                  <tr key={`off-${t}`} style={{ opacity: 0.6 }}>
                    <td className="mono">
                      {t} <Badge intent="bad">disabled</Badge>
                    </td>
                    <td className="nums">—</td>
                  </tr>
                ))}
                {byTrunk.length === 0 && s.trunksInactive.length === 0 && (
                  <tr>
                    <td colSpan={2} className="subtle">
                      No trunk carrying calls right now.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
